import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	IExternalCalendarMeetingInfo,
	IExternalCalendarMeetingsResponse,
} from '../../../../viewmodels/CalendarViewModels';
import { IDateRange } from '../utils/dateRange';

const BASE_QUERY_KEY = 'calendar/externalMeetings';

export interface IUseCalendarExternalMeetingsResult {
	meetings: IExternalCalendarMeetingInfo[];
	/** True when the fetch succeeded but the user has no connected calendar (nothing to overlay). */
	noCalendarConnected: boolean;
	/** True until the first response resolves (only while enabled). */
	isLoading: boolean;
}

/**
 * Fetch the user's external (non-Levitate) calendar meetings for the visible window. This is a live
 * provider call, so gate it behind an explicit opt-in (default off) via `enabled`.
 *
 * Omit `forUserId` for your own calendar; a teammate's id returns their meetings redacted to busy
 * blocks. Sent as a query param rather than via `withForUser` — the latter would run the request
 * *as* the teammate, bypassing the backend's permission gate and redaction.
 */
export function useCalendarExternalMeetings({
	range,
	forUserId,
	impersonationContext,
	enabled = true,
}: {
	range: IDateRange;
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}): IUseCalendarExternalMeetingsResult {
	const userSession = useUserSession();
	const startDate = range.start.toISOString();
	const endDate = range.end.toISOString();

	const query = useQuery({
		enabled: enabled && range.start < range.end,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IExternalCalendarMeetingsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { startDate, endDate, forUserId },
					urlPath: 'calendar/externalMeetings',
				}),
				'GET'
			),
		queryKey: [BASE_QUERY_KEY, startDate, endDate, forUserId, impersonationContext?.account?.id],
		meta: { toastErrorMessage: 'Failed to load external calendar meetings' },
		// Hits a live provider, so don't re-fetch on focus. 4xx (bad range, missing permission,
		// undeployed route) won't succeed on retry, so fail fast and let the toast surface.
		refetchOnWindowFocus: false,
		retry: (failureCount, error) => {
			const status = Api.asApiError(error)?.systemCode;
			if (typeof status === 'number' && status >= 400 && status < 500) {
				return false;
			}
			return failureCount < 2;
		},
	});

	return {
		meetings: query.data?.meetings ?? [],
		noCalendarConnected: query.data?.noCalendarConnected ?? false,
		isLoading: enabled ? query.isPending : false,
	};
}
